import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientViewsService } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Building2, Hash } from 'lucide-react';

function ClientViewCreatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [titleError, setTitleError] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const ids = searchParams.get('properties');
    if (ids) setPropertyIds(ids.split(','));
  }, [searchParams]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setTitleError('');
    setFormError('');
    if (!title.trim()) {
      setTitleError('Ingresa un título para la vista.');
      return;
    }
    setLoading(true);
    try {
      const clientView = await clientViewsService.create(title, propertyIds);
      navigate(`/client-views/${clientView.id}`);
    } catch {
      setFormError('Error al crear la vista. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">Nueva selección</span>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight">Crear vista</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Dale un nombre a esta selección de propiedades para tu cliente
          </p>
        </div>

        <form onSubmit={handleCreate} className="space-y-6">
          <div className="space-y-1.5">
            <Label htmlFor="title">Título de la vista</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setTitleError(''); }}
              placeholder="Ej: Opciones Colonia del Valle"
              required
            />
            {titleError && <p className="text-xs text-destructive">{titleError}</p>}
          </div>

          {propertyIds.length > 0 && (
            <div className="bg-white border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {propertyIds.length === 1 ? '1 propiedad seleccionada' : `${propertyIds.length} propiedades seleccionadas`}
                </p>
              </div>
            </div>
          )}

          {formError && <p className="text-sm text-destructive">{formError}</p>}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 transition-all active:scale-[0.98]"
            >
              {loading ? 'Creando...' : 'Crear vista'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default ClientViewCreatePage;
